import http from 'src/infrastructure/http'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import {
  AttributeGroup,
  GetShedsSearchLocationsPayload,
  GenericApiErrors,
  GetFiltersSearchLocationsPayload,
  FiltersSearchLocations,
  GetLocationsPayload,
  Locations,
  SelectedLocations,
  GetProductsPayload,
  Product,
  LocationHeightTypes,
  CreateFiltersData,
  CreateLocationsByCsvPayload,
} from './ShedManagementTypes'

const getAttributesGroup = async () => {
  try {
    const result = await http.get('/v2/warehouse/attributes')
    return parseGoodResponse<AttributeGroup[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getShedsSearchLocations = async (payload: GetShedsSearchLocationsPayload) => {
  try {
    const { fulfillmentCenterId } = payload
    const result = await http.get('/v2/warehouse/sheds', {
      params: {
        fulfillment_center_id: fulfillmentCenterId,
      },
    })
    return parseGoodResponse<string[]>(result.data.sheds)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getFiltersSearchLocations = async (payload: GetFiltersSearchLocationsPayload) => {
  try {
    const { fulfillmentCenterId, sheds } = payload
    const body = {
      fulfillment_center_id: fulfillmentCenterId,
      sheds,
    }
    const result = await http.post('/v2/warehouse/sheds/filters', body)
    return parseGoodResponse<FiltersSearchLocations>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getLocations = async (payload: GetLocationsPayload) => {
  try {
    const {
      fulfillmentCenterId, sheds, streets, shelves, bins, heightTypes, isGondola, isFresh,
    } = payload

    const body = {
      fulfillment_center_id: fulfillmentCenterId,
      sheds,
      streets,
      shelves,
      bins,
      height_types: heightTypes || [],
      is_gondola: isGondola || [],
      is_fresh: isFresh || [],
    }

    const result = await http.post('/v2/warehouse/location', body)
    return parseGoodResponse<Locations[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const saveAttributesLocations = async (payload: SelectedLocations[]) => {
  try {
    const body = payload.map((item) => {
      const {
        id, isGondola, isFresh, idLocationHeightType, locationAttribute,
      } = item

      return {
        id,
        is_gondola: isGondola,
        is_fresh: isFresh,
        id_location_height_type: idLocationHeightType,
        location_attribute: locationAttribute.map((attr) => ({
          id_attribute: attr.idAttribute,
        })),
      }
    })

    const result = await http.put('/v2/warehouse/location', body)
    return parseGoodResponse<SelectedLocations[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getProducts = async (payload: GetProductsPayload) => {
  try {
    const { productsCodes, productDescription } = payload

    const body = {
      products_codes: productsCodes,
      search_text: productDescription,
    }

    const result = await http.post('/v2/warehouse/products', body)
    return parseGoodResponse<Product[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const saveAttributesProducts = async (payload: Product[]) => {
  try {
    const body = payload.map((item) => {
      const {
        idModel,
        idAttributeType,
        idAttributeFunction,
        idAttributeStorage,
        allowHighStorage,
        fractioningTag,
        dueDateType,
      } = item

      return {
        id_model: idModel,
        id_attribute_type: idAttributeType,
        id_attribute_function: idAttributeFunction,
        id_attribute_storage: idAttributeStorage,
        allow_high_storage: allowHighStorage,
        fractioning_tag: fractioningTag,
        due_date_type: dueDateType,
      }
    })

    const result = await http.put('/v2/warehouse/products', body)
    return parseGoodResponse<Product[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getLocationHeightTypes = async () => {
  try {
    const result = await http.get('/v2/warehouse/location/height-types')
    return parseGoodResponse<LocationHeightTypes[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getCreateFilters = async () => {
  try {
    const result = await http.get('/v2/warehouse/location/create-filters')
    return parseGoodResponse<CreateFiltersData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const createLocationsByCsv = async (payload: CreateLocationsByCsvPayload) => {
  try {
    const formData = new FormData()
    formData.append('id_fulfillment_center', payload.id_fulfillment_center.toString())
    formData.append('id_location_type', payload.id_location_type.toString())
    formData.append('id_location_height_type', payload.id_location_height_type.toString())
    formData.append('planilha', payload.planilha)

    const result = await http.post('/v2/warehouse/location/create-by-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return parseGoodResponse<any>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getAttributesGroup,
  getShedsSearchLocations,
  getFiltersSearchLocations,
  getLocations,
  saveAttributesLocations,
  getProducts,
  saveAttributesProducts,
  getLocationHeightTypes,
  getCreateFilters,
  createLocationsByCsv,
}
